import {HttpStatusCode} from 'axios';
import {
    find, keys, map, isEmpty, trim, trimStart, isFunction, filter, some, debounce, without,
    size, last
} from 'lodash';
import {Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {useBeforeunload} from 'react-beforeunload';
import {useTranslation} from 'react-i18next';
import {useSound} from 'use-sound';

import {notifier, InvalidFileTypeModal} from 'components';
import {useIsChanged, usePrevious} from 'hooks';
import {SessionActivityName, SessionActivityTextChatData} from 'interfaces/session';
import {TRANSLATION_KEYS} from 'locale';
import {TextChatMessage, TextChatContentType, ApiError, ComplianceDecision, ComplianceDecisionMeta} from 'types';
import {apiUtils, logger, sessionUtils, userAuthUtils} from 'utils';

import {SessionTextChatView} from './SessionTextChatView';
import {ComplianceFlaggedState, SessionTextChatProps, SubmitMessageResult} from './types';

const SOUND_URL = '/assets/sounds/message.mp3';
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB max
const DEBOUNCE_TIMEOUT = 500;
const DEBOUNCE_MAX_WAIT = 1000;
const T_KEYS = TRANSLATION_KEYS.COMPONENTS.SESSION_TEXT_CHAT;

export const SessionTextChatBase: React.FC<SessionTextChatProps> = ({
    session, threads, downloadFileDisabled: downloadFileDisabledProp, onSubmitMessage, interactiveChatState,
    onChangeState, aiSuggestionsEnabled, onNewMessage: onNewMessageProp, TextChatView = SessionTextChatView, onFileDownload, dockedPanel,
    requestAiSuggestions, aiSuggestionsLoading, aiSuggestions, aiSuggestionsError, activityName = SessionActivityName.TextChat,
    disableNewMessageSound, uploadFileHidden, voiceInput, inputDisabled
}) => {
    const {t} = useTranslation();

    let sendFileRef = useRef(null);
    const debounceCanelableRef = useRef(null);
    const messagesEndRef = useRef(null);
    const prevMessagesEndRef = usePrevious(messagesEndRef);

    const [message, setMessage] = useState<string>('');
    const [optionSelectInProgress, setOptionSelectInProgress] = useState(false);
    const [complianceFlagged, setComplianceFlagged] = useState<ComplianceFlaggedState>(null);
    const [sendInProgress, setSendInProgress] = useState(false);
    const complianceResendRef = useRef(false);

    const [fileIsUploading, setFileIsUploading] = useState(false);
    const [downloadingFiles, setDownloadingFiles] = useState<string[]>([]);

    const [playNewMessageSound] = useSound(SOUND_URL, {volume: 0.5});

    const activity = useMemo(() => find(session?.activities, {name: activityName}), [session, activityName]);
    const activityChanged = useIsChanged(activity?.id);
    const activityData = useMemo(() => activity?.data as SessionActivityTextChatData, [activity]);
    const isInteractiveChatInProgress = useMemo(() => interactiveChatState?.inProgress, [interactiveChatState]);
    const isChatbotActive = useMemo(() => interactiveChatState?.chatbotActive, [interactiveChatState]);
    const formDisabled = useMemo(
        () => inputDisabled || (isInteractiveChatInProgress && !isChatbotActive),
        [inputDisabled, isInteractiveChatInProgress, isChatbotActive]
    );
    const submitDisabled = useMemo(
        () => formDisabled || sendInProgress || trim(message).length === 0,
        [message, formDisabled, sendInProgress]
    );
    const downloadFileDisabled = useMemo(() =>
        downloadFileDisabledProp || userAuthUtils.isDownloadFileDisabledAuthSource(), [downloadFileDisabledProp]);
    const localParticipantId = useMemo(() => session?.localParticipant.id, [session]);
    const userParticipantIds = useMemo(() => sessionUtils.getUserParticipantIdGroups(session), [session]);

    const isTypingLocal = useMemo(() =>
        !!activityData?.state?.[localParticipantId]?.typing, [localParticipantId, activityData]);

    const typingRemoteParticipantIds: string[] = useMemo(() => keys(activityData?.state)
        .filter((participantId) => participantId !== localParticipantId)
        .filter((participantId) => !!activityData.state[participantId].typing), [activityData, localParticipantId]);

    const typingRemoteParticipantIdsChanged = useIsChanged(typingRemoteParticipantIds);

    const [prevActivitiesMessages, setPrevActivitiesMessages] = useState<TextChatMessage[]>([]);

    const messages: TextChatMessage[] = useMemo(() => {
        const maxIndex = last(prevActivitiesMessages)?.index || 0;
        const currentActivitiesMessages = sessionUtils.getTextChatActivityMessages(activityData?.threads, threads)
            .map((msg) => ({...msg, index: msg.index + maxIndex}));
        return [
            ...prevActivitiesMessages,
            ...currentActivitiesMessages
        ];
    }, [activityData, prevActivitiesMessages, threads]);
    const prevMessages = usePrevious(messages);

    const onLocalTypingChange = useCallback((typing: boolean) => {
        if (typing !== isTypingLocal) {
            onChangeState?.({typing});
        }
    }, [isTypingLocal, onChangeState]);
    useBeforeunload(() => onLocalTypingChange(false));

    const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
        messagesEndRef.current?.scrollIntoView({behavior});
    }, [messagesEndRef]);

    const [invalidFileTypeModalProps, setInvalidFileTypeModalProps] = useState<{
        open: boolean;
        error?: ApiError;
    }>({open: false});
    const onCloseInvalidFileTypeModal = useCallback(() => {
        setInvalidFileTypeModalProps({open: false});
    }, []);

    const handleMessageChange = useCallback((msg: string) => {
        const newMessage = trimStart(msg);
        setMessage(newMessage);
        if (debounceCanelableRef.current) {
            debounceCanelableRef.current.cancel();
        }
        debounceCanelableRef.current =
            debounce(() => onLocalTypingChange(!isEmpty(newMessage)), DEBOUNCE_TIMEOUT, {maxWait: DEBOUNCE_MAX_WAIT});
        (debounceCanelableRef as any).current();
    }, [onLocalTypingChange]);

    const submitMessage = useCallback(async (msg: Partial<TextChatMessage>, compliance?: ComplianceDecisionMeta) => {
        if (isFunction(onSubmitMessage)) {
            handleMessageChange('');
            setComplianceFlagged(null);
            setSendInProgress(true);
            try {
                const result: SubmitMessageResult = await onSubmitMessage(msg, compliance);
                const complianceResult = result?.compliance;
                if (complianceResult?.flagged) {
                    setComplianceFlagged({result: complianceResult, originalContent: msg.content});
                    // The check takes ~1-2s; don't clobber a draft the agent started typing meanwhile.
                    setMessage((current) => current || msg.content);
                }
            } catch (error: any) {
                notifier.error({
                    title: t(T_KEYS.ERROR.COMMON),
                    ...(error?.response?.status === HttpStatusCode.TooManyRequests && {
                        details: t(T_KEYS.ERROR.TOO_MANY_REQUESTS)
                    })
                });
                // eslint-disable-next-line no-console
                console.error(error);
            } finally {
                setSendInProgress(false);
                scrollToBottom();
            }
        }
    }, [onSubmitMessage, handleMessageChange, scrollToBottom, t]);

    const handleSubmit = useCallback(async (event: React.FormEvent<HTMLFormElement>) => {
        event?.preventDefault?.();
        if (!submitDisabled) {
            await submitMessage({
                content: trim(message),
                contentType: TextChatContentType.Text
            });
        }
    }, [submitDisabled, submitMessage, message]);

    // Ref guard: rapid double-clicks on the panel buttons must not double-send the decision resend.
    const resendWithDecision = useCallback(async (content: string, decision: ComplianceDecision) => {
        if (complianceResendRef.current) {
            return;
        }
        complianceResendRef.current = true;
        try {
            await submitMessage(
                {content, contentType: TextChatContentType.Text},
                {verdictId: complianceFlagged.result.verdictId, decision}
            );
        } finally {
            complianceResendRef.current = false;
        }
    }, [complianceFlagged, submitMessage]);

    const handleComplianceAccept = useCallback(
        () => resendWithDecision(complianceFlagged.result.suggestedRewording, ComplianceDecision.Accepted),
        [complianceFlagged, resendWithDecision]
    );

    const handleComplianceIgnore = useCallback(
        () => resendWithDecision(complianceFlagged.originalContent, ComplianceDecision.Ignored),
        [complianceFlagged, resendWithDecision]
    );

    // Dismiss = send nothing: close the panel, keep the text in the input so the agent can edit or drop it.
    const handleComplianceDismiss = useCallback(() => {
        setComplianceFlagged(null);
    }, []);

    const handleOptionSelect = useCallback(async (index: number) => {
        const lastMessage = last(messages);
        const selectedOption = lastMessage?.data?.options?.[index];

        if (selectedOption) {
            try {
                setOptionSelectInProgress(true);
                await submitMessage({
                    content: trim(selectedOption.content),
                    contentType: selectedOption.contentType,
                    data: {
                        chatOptionIndex: index,
                        nodeValue: selectedOption.nodeValue
                    }
                });
            } finally {
                setOptionSelectInProgress(false);
            }
        }
    }, [messages, submitMessage]);

    const onNewMessage = useCallback(() => {
        if (isFunction(onNewMessageProp)) {
            onNewMessageProp();
        }
        if (!disableNewMessageSound) {
            playNewMessageSound();
        }
    }, [disableNewMessageSound, onNewMessageProp, playNewMessageSound]);

    const openFileFileInput = useCallback(() => {
        sendFileRef?.current?.click?.();
    }, [sendFileRef]);

    const handleUploadFile = useCallback(async (buffer: ArrayBuffer, file: File) => {
        if (file.size > MAX_FILE_SIZE) {
            notifier.error({details: t(T_KEYS.FILE_ERROR.MAX_SIZE_EXCEEDED)});
            return;
        }

        setFileIsUploading(true);

        try {
            const base64 = new Uint8Array(buffer).reduce((data, byte) => data + String.fromCharCode(byte), '');
            await onSubmitMessage({
                content: file.name,
                contentType: TextChatContentType.File,
                base64: btoa(base64)
            });
        } catch (e: any) {
            const error = e as ApiError;
            if (apiUtils.isFileTypeError(error)) {
                setInvalidFileTypeModalProps({open: true, error});
            } else {
                notifier.error({details: t(T_KEYS.FILE_ERROR.UPLOAD_FAILED)});
            }
            logger.error(error);
        } finally {
            setFileIsUploading(false);
            scrollToBottom();
        }
    }, [onSubmitMessage, t, scrollToBottom]);

    const downloadFile = useCallback(async ({id, content: fileName}: Partial<TextChatMessage>) => {
        try {
            setDownloadingFiles((prev) => [...prev, id]);
            if (isFunction(onFileDownload)) {
                await onFileDownload(fileName, activity?.id);
            }
        } catch (error: any) {
            notifier.error({details: t(T_KEYS.FILE_ERROR.DOWNLOAD_FAILED)});
            logger.error(error);
        } finally {
            setDownloadingFiles((prev) => without(prev, id));
        }
    }, [t, onFileDownload, activity]);

    const requestAiSuggestionsTextChat = useCallback(() =>
        requestAiSuggestions(), [requestAiSuggestions]);

    const getTranslation = useCallback((msg: Partial<TextChatMessage>) => {
        const localLanguage = activityData?.translationSettings?.[localParticipantId]?.language;
        if (msg.from.id !== localParticipantId && localLanguage) {
            return activityData?.translation?.[localLanguage]?.[msg.from.id]?.[msg.id]?.content || msg.content;
        }
        return msg.content;
    }, [activityData, localParticipantId]);

    useEffect(() => {
        if (activityChanged) {
            setPrevActivitiesMessages(sessionUtils.getPrevTextChatMessages(activityData));
        }
    }, [prevActivitiesMessages, activityData, activityChanged]);

    useEffect(() => {
        if (typingRemoteParticipantIdsChanged) {
            scrollToBottom();
        }
    }, [scrollToBottom, typingRemoteParticipantIdsChanged]);

    // The panel mounts on the render AFTER submitMessage's scroll and shrinks the list viewport —
    // re-scroll once it is actually in the layout, or the newest messages end up hidden behind it.
    useEffect(() => {
        if (complianceFlagged) {
            scrollToBottom();
        }
    }, [complianceFlagged, scrollToBottom]);

    useEffect(() => {
        if (size(prevMessages) < size(messages)) {
            const newMessages = filter(messages, (msg) => !map(prevMessages, 'id').includes(msg.id));
            const hasRemoteMessages = some(newMessages, (msg) => msg.from.id !== localParticipantId);
            if (!isEmpty(newMessages)) {
                if (hasRemoteMessages) {
                    onNewMessage();
                }
                scrollToBottom();
            }

        }
    }, [prevMessages, messages, localParticipantId, onNewMessage, scrollToBottom]);

    useLayoutEffect(() => {
        if (messagesEndRef && !prevMessagesEndRef) {
            setTimeout(() => {
                scrollToBottom();
            });
        }
    }, [messagesEndRef, prevMessagesEndRef, scrollToBottom]);

    return (
        <Fragment>
            <TextChatView
                dockedPanel={dockedPanel}
                voiceInput={voiceInput}
                message={message}
                messages={messages}
                participants={session.participants}
                getTranslation={getTranslation}
                fileIsUploading={fileIsUploading}
                downloadingFiles={downloadingFiles}
                typing={isTypingLocal}
                localParticipantId={localParticipantId}
                typingRemoteParticipantIds={typingRemoteParticipantIds}
                userParticipantIds={userParticipantIds}
                submitDisabled={submitDisabled}
                sendInProgress={sendInProgress}
                messagesEndRef={messagesEndRef}
                setSendFileInputRef={(ref) => sendFileRef = ref}
                downloadFileDisabled={downloadFileDisabled}
                formDisabled={formDisabled}
                optionSelectInProgress={optionSelectInProgress}
                downloadFile={downloadFile}
                aiSuggestionsEnabled={aiSuggestionsEnabled}
                aiSuggestions={aiSuggestions}
                aiSuggestionsLoading={aiSuggestionsLoading}
                aiSuggestionsError={aiSuggestionsError}
                uploadFileHidden={uploadFileHidden}
                complianceFlagged={complianceFlagged}
                requestAiSuggestions={requestAiSuggestionsTextChat}
                handleUploadFile={handleUploadFile}
                openFileFileInput={openFileFileInput}
                onComplianceAccept={handleComplianceAccept}
                onComplianceIgnore={handleComplianceIgnore}
                onComplianceDismiss={handleComplianceDismiss}
                onChange={handleMessageChange}
                onOptionSelect={handleOptionSelect}
                onSubmit={onSubmitMessage ? handleSubmit : null}
            />
            <InvalidFileTypeModal {...invalidFileTypeModalProps} onClose={onCloseInvalidFileTypeModal} />
        </Fragment>
    );
};
